import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Mail } from 'lucide-react';

const UserStatusCheck = ({ children }) => {
  const { user, signOut } = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) {
        setIsChecking(false);
        return;
      }

      try {
        // Check profiles table for is_active status
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('is_active')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Error checking user status:', profileError);
          setIsChecking(false);
          return;
        }

        // User is disabled if profiles table shows is_active = false
        const userDisabled = profile?.is_active === false;
        setIsDisabled(userDisabled);

        if (userDisabled) {
          // Sign out the user immediately
          await signOut();
        }
      } catch (error) {
        console.error('Error checking user status:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkUserStatus();
  }, [user, signOut]);

  if (isChecking) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isDisabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <CardTitle className="text-xl text-red-600">Account Disabled</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Your account has been disabled by an administrator. 
              Please contact system administrators for more information.
            </p>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => window.location.href = 'mailto:support@urban-mint.com?subject=Account Disabled - Help Request'}
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
              <Button 
                variant="ghost" 
                className="w-full"
                onClick={() => window.location.href = '/'}
              >
                Return to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return children;
};

export default UserStatusCheck;
